/**
 * Allowlist sanitizer for HTML rendered with v-html from stored content
 * (notifications, announcements). It keeps the formatting the kiosk TinyMCE
 * editor produces and strips anything that can run script: unknown tags are
 * unwrapped, dangerous ones dropped with their content, and only a few
 * attributes survive, with URL schemes limited to http(s), mailto and tel.
 *
 * DOMParser documents are inert, so nothing in the input runs or loads while
 * it is being cleaned.
 */

const ALLOWED_TAGS = new Set([
    'A', 'B', 'BLOCKQUOTE', 'BR', 'CODE', 'DIV', 'EM', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
    'HR', 'I', 'IMG', 'LI', 'OL', 'P', 'PRE', 'S', 'SMALL', 'SPAN', 'STRIKE', 'STRONG',
    'SUB', 'SUP', 'TABLE', 'TBODY', 'TD', 'TFOOT', 'TH', 'THEAD', 'TR', 'U', 'UL',
]);

const DROPPED_TAGS = new Set([
    'BASE', 'BUTTON', 'EMBED', 'FORM', 'FRAME', 'FRAMESET', 'IFRAME', 'INPUT', 'LINK',
    'MATH', 'META', 'NOSCRIPT', 'OBJECT', 'SCRIPT', 'SELECT', 'STYLE', 'SVG', 'TEMPLATE',
    'TEXTAREA',
]);

const GLOBAL_ATTRIBUTES = new Set(['style', 'title']);

const TAG_ATTRIBUTES: Record<string, Set<string>> = {
    A: new Set(['href', 'target', 'rel']),
    IMG: new Set(['src', 'alt', 'width', 'height']),
    TD: new Set(['colspan', 'rowspan']),
    TH: new Set(['colspan', 'rowspan']),
};

const URL_ATTRIBUTES = new Set(['href', 'src']);

const URL_SCHEME = /^([a-z][a-z0-9+.-]*):/i;

const SAFE_SCHEMES = new Set(['http', 'https', 'mailto', 'tel']);

// Browsers ignore control characters and whitespace inside a URL scheme
// ("java\tscript:"), so compare against the value with them removed.
// eslint-disable-next-line no-control-regex
const IGNORED_URL_CHARACTERS = /[\u0000-\u0020\u007f]/g;

// URLs without a scheme are relative (paths, queries, fragments) and resolve
// against the current page, so only an explicit scheme needs checking.
const isSafeUrl = (value: string): boolean => {
    const scheme = value.replace(IGNORED_URL_CHARACTERS, '').match(URL_SCHEME)?.[1];

    return scheme === undefined || SAFE_SCHEMES.has(scheme.toLowerCase());
};

const cleanAttributes = (element: Element, tag: string): void => {
    for (const attribute of Array.from(element.attributes)) {
        const name = attribute.name.toLowerCase();
        const allowed = GLOBAL_ATTRIBUTES.has(name) || TAG_ATTRIBUTES[tag]?.has(name);

        if (!allowed || (URL_ATTRIBUTES.has(name) && !isSafeUrl(attribute.value))) {
            element.removeAttribute(attribute.name);
        }
    }

    if (tag === 'A' && element.getAttribute('target') === '_blank') {
        element.setAttribute('rel', 'noopener noreferrer');
    }
};

const cleanChildren = (parent: Element): void => {
    for (const child of Array.from(parent.children)) {
        const tag = child.tagName.toUpperCase();

        if (DROPPED_TAGS.has(tag)) {
            child.remove();
            continue;
        }

        cleanChildren(child);

        if (!ALLOWED_TAGS.has(tag)) {
            child.replaceWith(...Array.from(child.childNodes));
            continue;
        }

        cleanAttributes(child, tag);
    }
};

export const sanitizeHtml = (html: string | null | undefined): string => {
    if (!html) {
        return '';
    }

    const document = new DOMParser().parseFromString(html, 'text/html');
    cleanChildren(document.body);

    return document.body.innerHTML;
};
